import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { resources } from './resources';
import {
    DEFAULT_LANGUAGE,
    LANGUAGE_STORAGE_KEY,
    LANGUAGES,
    SUPPORTED_LANGS,
    getLanguageDir,
    isValidLanguage,
} from './languages';
import type { LanguageCode } from './languages';

export const I18N_STORAGE_KEY = LANGUAGE_STORAGE_KEY;

/**
 * Read the persisted language from localStorage.
 */
export function getSavedLanguage(): LanguageCode | null {
    try {
        const saved = localStorage.getItem(I18N_STORAGE_KEY);
        if (isValidLanguage(saved)) return saved as LanguageCode;
    } catch {
        // localStorage unavailable — ignore
    }
    return null;
}

/**
 * Detect the best default language: Telegram user language, then browser, then English.
 */
export function detectLanguage(): LanguageCode {
    if (typeof window === 'undefined') return DEFAULT_LANGUAGE;

    try {
        const saved = getSavedLanguage();
        if (saved) return saved;

        let candidate = '';

        // Telegram Mini App user language code takes priority
        const tgUserLang = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
        if (tgUserLang) candidate = tgUserLang.toLowerCase();

        if (!candidate) candidate = navigator.language?.toLowerCase() ?? '';

        const normalized = candidate.split('-')[0];

        // Direct match on root code first
        if (isValidLanguage(normalized)) return normalized as LanguageCode;

        // Alias/partial matches (e.g. "rs" -> "ru", "ir" -> "fa", "cn" -> "zh-CN")
        const aliases: Record<string, string> = {
            rs: 'ru',
            by: 'ru',
            be: 'ru',
            ir: 'fa',
            cn: 'zh-CN',
            chs: 'zh-CN',
            kp: 'ko',
            ua: 'uk',
        };
        const alias = aliases[normalized];
        if (alias && isValidLanguage(alias)) return alias as LanguageCode;
    } catch {
        // fall through to default
    }

    return DEFAULT_LANGUAGE;
}

/**
 * Persist the selected language.
 */
export function persistLanguage(language: LanguageCode): void {
    try {
        localStorage.setItem(I18N_STORAGE_KEY, language);
    } catch {
        // ignore
    }
}

/**
 * Apply the document-level direction/lang attributes (RTL for Arabic/Persian).
 */
export function applyDocumentLanguage(language: LanguageCode): void {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('dir', 'ltr');
}

export function initI18n() {
    const language = detectLanguage();

    if (!i18n.isInitialized) {
        i18n.use(initReactI18next).init({
            resources,
            lng: language,
            fallbackLng: DEFAULT_LANGUAGE,
            ns: Object.keys(resources.en) as string[],
            defaultNS: 'common',
            nsSeparator: ':',
            keySeparator: '.',
            interpolation: {
                escapeValue: false,
            },
            returnNull: false,
            returnEmptyString: false,
            react: {
                useSuspense: false,
                bindI18n: 'languageChanged',
                bindI18nStore: '',
            },
        });
    } else {
        i18n.changeLanguage(language);
    }

    applyDocumentLanguage(language);
    return i18n;
}

/**
 * Switch language at runtime: updates i18next, persists the choice,
 * and refreshes document lang without flipping layout direction.
 */
export function setAppLanguage(language: LanguageCode): void {
    if (!isValidLanguage(language)) return;
    try {
        document.documentElement.setAttribute('dir', 'ltr');
        document.documentElement.setAttribute('lang', language);
        persistLanguage(language);
        void i18n.changeLanguage(language);
    } catch {
        // ignore
    }
}

export { i18n, LANGUAGES, SUPPORTED_LANGS, DEFAULT_LANGUAGE };
export type { LanguageCode };